/**
 * UI state that is not related to a MERODE model (i.e. not part of the MERODE
 * uimodel).
 */
import { getParent, types, type Instance } from 'mobx-state-tree';
import UIEDG from './edg';
import UIOET from './oet';
import UIFSM from './fsm';
import Inspector from './inspector';
import UIProject from './project';
import type { IMetaModel } from '../model/metamodel';

/** The boolean panel-visibility keys toggled by `togglePanel`. */
type PanelKey = 'edg' | 'oet' | 'fsms';

// `highlightedPath` / `highlightedMethods` reach the active model version's
// metamodel through the RootStore (one level up). Declare just that path to
// avoid an import cycle with the RootStore (which embeds the UI store).
interface IUIRoot {
  readonly projects: {
    readonly activeModelVersion:
      | {
          readonly contentAvailable: boolean;
          readonly content: { readonly metamodel: IMetaModel } | null;
        }
      | null
      | undefined;
  };
}

/**
 * Visibility of the views
 */
const UIVisibility = types
  .model('UIVisibility', {
    modelExplorer: true,
    edg: true,
    oet: false,
    fsms: false,
    inspector: false,
    // The FSM that is visible in the FSMs view
    fsm: types.maybe(types.string),
  })
  .views((self) => ({
    get nbPanels() {
      let screens = 0;
      if (self.edg) {
        screens++;
      }
      if (self.oet) {
        screens++;
      }
      if (self.fsms) {
        screens++;
      }
      return screens;
    },
  }))
  .actions((self) => {
    // `toggleEDG`/`toggleOET`/`toggleFSMs` call the sibling action `togglePanel`
    // from this same block; expose it through an interface alias so the forward
    // reference types (§4). The dynamic panel indexing is done through a uniform
    // boolean record (MST narrows the write at runtime — byte-identical).
    const selfActions = self as unknown as { togglePanel(key: PanelKey): void };
    const panels = self as unknown as Record<PanelKey, boolean>;
    return {
    toggleModelExplorer() {
      self.modelExplorer = !self.modelExplorer;
    },
    togglePanel(key: PanelKey) {
      if (panels[key] && self.nbPanels > 1) {
        // If the panel is visible and it is not the only panel -> hide
        panels[key] = false;
      } else if (!panels[key] && self.nbPanels < 2) {
        // If the panel is not visible and there is only 1 panel visible -> show
        panels[key] = true;
      } else if (self.nbPanels === 2) {
        // If 2 panels visible, swap panels
        let otherPanel: PanelKey | undefined;
        if (self['oet']) {
          otherPanel = 'oet';
        } else if (self['fsms']) {
          otherPanel = 'fsms';
        } else if (self['edg']) {
          otherPanel = 'edg';
        }
        panels[otherPanel!] = !panels[otherPanel!];
        panels[key] = !panels[key];
      } // else: do nothing
    },
    toggleEDG() {
      selfActions.togglePanel('edg');
    },
    toggleOET() {
      selfActions.togglePanel('oet');
    },
    toggleFSMs() {
      selfActions.togglePanel('fsms');
    },
    toggleInspector() {
      self.inspector = !self.inspector;
    },
    showInspector() {
      self.inspector = true;
    },
    hideInspector() {
      self.inspector = false;
    },
    showEDG() {
      self.oet = false;
      self.fsms = false;
      self.edg = true;
    },
    showOET() {
      self.edg = false;
      self.fsms = false;
      self.oet = true;
    },
    showFSMs() {
      self.edg = false;
      self.oet = false;
      self.fsms = true;
    },
    showFSM(fsm?: string) {
      self.fsm = fsm;
    },
    };
  });

/**
 * Root for the UI store.
 *
 * - visible: see above
 * - edg: state related to the edg view (see `edg.js`)
 * - oet: state related to the oet view (see `oet.js`)
 * - project: state related to whole project view (see `project.js`)
 * - inspector: state related to the inspector view (see `inspector.js`)
 * - selected: id of the currently selected element
 * - isTouch: whether or not the device is a touch device (defaults to false and
 *   is set to true by an event listener listening for 'touchstart', see the
 *   top-level `index.js` file)
 */
const UI = types
  .model('UI', {
    visible: types.optional(UIVisibility, {}),
    edg: types.optional(UIEDG, {}),
    oet: types.optional(UIOET, {}),
    fsm: types.optional(UIFSM, {}),
    project: types.optional(UIProject, {}),
    inspector: types.optional(Inspector, {}),
    selected: types.maybeNull(types.string),
    isTouch: false,
  })
  .views((self) => ({
    get selectedMPC() {
      return self.inspector.selectedMPC;
    },
    get addedMPCs() {
      return self.inspector.addedMPCs;
    },
    /**
     * Calculate the ids of the dependencies/inheritances that should be highlighted when a
     * method is selected.
     */
    get highlightedPath() {
      const selected = self.selected;
      const root = getParent<IUIRoot>(self, 1);
      if (root.projects.activeModelVersion && root.projects.activeModelVersion.contentAvailable) {
        const metamodel = root.projects.activeModelVersion.content!.metamodel;
        // `selected` may be null (nothing selected); legacy passes it straight to
        // the lookup, which returns undefined for a null/unknown id.
        const selectedMethod = metamodel.getMethodById(selected as string);
        if (selectedMethod != null) {
          if (selectedMethod.provenance === 'SPECIALISED') {
            // For specialised methods, highlight the inheritance along which the method that is
            // specialised was inherited. Legacy assumes a specialised method has
            // a viaMethod with a viaInheritance.
            return [selectedMethod.viaMethod!.viaInheritance!.id];
          } else {
            // Highlight the propagation path (defined for non-specialised methods).
            return selectedMethod.propagationPath!.map((e) => e.id);
          }
        } else {
          return [];
        }
      } else {
        return [];
      }
    },
    /**
     * Calculate the ids of the methods along the propagationPath of the currently selected method.
     */
    get highlightedMethods() {
      const selected = self.selected;
      const root = getParent<IUIRoot>(self, 1);
      if (root.projects.activeModelVersion && root.projects.activeModelVersion.contentAvailable) {
        const metamodel = root.projects.activeModelVersion.content!.metamodel;
        const selectedMethod = metamodel.getMethodById(selected as string);
        if (selectedMethod != null) {
          if (selectedMethod.provenance === 'SPECIALISED') {
            // For the specialised methods, highlight the viaMethod.
            return [selectedMethod.viaMethod!.id];
          } else {
            return selectedMethod.propagationPathMethods!.map((m) => m.id);
          }
        } else {
          return [];
        }
      } else {
        return [];
      }
    },
  }))
  .actions((self) => ({
    /**
     * Select or deselect an element. Open the inspector on selection (if
     * openInpector is true), hide on deselection. Clear the selections within
     * the inspector if the selected element changes.
     *
     * @param {String}  id Id of the selected element. null to deselect
     * @param {Boolean} toggle Toggle or set?
     * @param {Boolean} openInspector Should the inspector open on selection?
     */
    select(id: string | null, toggle = true, openInspector = true) {
      const wasSelected = self.selected;
      if (self.selected === id && toggle) {
        self.selected = null;
      } else {
        self.selected = id;
      }
      if (self.selected !== null && openInspector) {
        self.visible.showInspector();
      } else {
        self.visible.hideInspector();
      }
      if (wasSelected !== self.selected) {
        self.inspector.clearSelections();
      }
    },
    setIsTouch(isTouch: boolean) {
      self.isTouch = isTouch;
    },
  }));

export type IUIVisibility = Instance<typeof UIVisibility>;
export type IUI = Instance<typeof UI>;

export default UI;
